import React, { useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useGetPatientLabQuery, useGetUsersQuery } from "store";
import {
    Button,
    Card,
    CardHeader,
    CardBody,
    CardFooter,
    Text,
    Box,
    Link as ChakraLink,
    Grid,
    GridItem,
    Table,
    Tbody,
    Tr,
    Td,
    Spinner,
    Center,
    Flex,
} from "@chakra-ui/react";
import { Helmet } from "react-helmet-async";
import moment from "moment";
import dayjs from "dayjs";
import jsPDF from "jspdf";
import rxIcon from "assets/img/rx1.jpg";
import sigmaLogo from "assets/img/Sigma Logo.jpg";

const LabDetails = () => {
    const {patientId, labId} = useParams();
    const {
        data: lab,
        error,
        isLoading,
    } = useGetPatientLabQuery({
        patientId,
        labId
    });

    const navigate = useNavigate();

    const handleUpdateClick = (patientId, labId) => {
        if (patientId && labId) {
            navigate(`/patients/${patientId}/labs/${labId}/update`);
        }
        else {
            console.error("Invalid patientId or presId");
        }
    }

    const Day = moment (lab?.date, "YYYY,MM,DD").toDate(); 

    const formattedDay = Day.toLocaleDateString("en-US", {
        day: "numeric",
        month: "long",
        year: "numeric",
    }); 

    const time = lab?.time;
    const formattedTime = moment(time, "HH:mm:ss.SSSSSS").format("h:mm A");

    // Fetch users data
    const { data: users, isLoading: isLoadingUsers } = useGetUsersQuery();

    const userMap = useMemo(() => {
        const map = {};
        if (users && users.results) {
            users.results.forEach((user) => {
                if (user.id && user.username) {
                    map[user.id] = user.username;
                }
            });
        }
        return map;
    }, [users]); 

    const calculateAge = (dateOfBirth) => {
        if (!dateOfBirth) return "Unknown"; // Handle cases where the date_of_birth is missing
        const birthDate = moment(dateOfBirth);
        const today = moment();
        return today.diff(birthDate, "years"); // Calculate the difference in years
    };

    const generatePDF = (lab) => {
        if (!lab || !lab.patient_details) return;
        const patient = lab.patient_details;

        const signature =
            "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAALoAAABLBAMAAAA17oB+AAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAAtUExURf///5mZmURERO7u7qqqqgAAADMzMxkZGd3d3WZmZlVVVczMzIiIiHd3d7u7uwk2SxEAAAAJcEhZcwAADsMAAA7DAcdvqGQAAAPmSURBVFjD7ZfPaxNBFMcn2TbZNO0SWlbqD8SDa8AfkCKhlNISKTmoVWqa6Zq0KUVIg4VCW9nGRiupupQeKgEN9SJUNFQKQi69eJAerNBLCUqw6KWgQUrxf3B+7Ga3v5uYvcg+yO7MsPnsd95782YWANNMM80000wzzRCr9xgIr+oxkm4dMRAOvC58nVlcbDSC7s+iSy2Mx+E7A+gL+BJ9DsAsXK44nI1g6UHcbAlXPL6ObnSRU6TNP6g03T6PLgIV7ZSSFaYXNhD1ltKZHqgwfcWjS3kuXGH6OfRbcqk9/zx4tvqqUYsu+2/0BPrli7SWwAIUJdjzQ4VL9/HNkigPzmKXD6qdz1CKnPaAp18gTSIwPRrAt3T0TFl0y210idA2J/SsNfWRZluATkdI8km8JrhAWXQbyhJLL2nOSg8RKe8jHT9JfS4IZBQTaxfgXeXQq3NqyszAs1ivtZ+MOyVcfqZHSJHzu0BNfzl0GUn14vrSAtfpSDRLbs3olUw8SbS7UT9eDh0vJvwGkHmp+uoeFR8CoOY2KdAsjoVSLJC3ho9O5z20BteqyxWwIXrPZ4mzkfYaXIocQVVP8Oj0bx46cX+qOJSnrqlOgXFAtMs53I8p0qUSkvMOoBOPaUPN1DW2AQ6rRNoXPKoPkf2GJVS6hJLyC9oQS4tNbTeJANJOXWUnmeWEJTgGTCCP4r8VsrpYkD2K6yUukl0s5XFkna3A1J4cdmsfug07slrnzRqyupgxsoS9riq62EAGOcgRiu/ev5hfV+MS9O1N9+KgWXQTZihBIK6QXQ6lPqPgOoXOnauq/Tja78Xz7qG9wpFQ0h0IuncvkdkLy1S7XZlWXehU+pE/p/936w03Ir/fSu5TqZF2nrzVqhNP9loQ81DtvBqSNIzothfm46obwqG1g1II0eeUcqidN0iOOinI6xsv8i4DuRieP1Eo3v10SHZOkJQnsQxrj+KC76B0uaNvV0QAM5WBwzcPP58g9KTS5EPFx7Gz7Ir2673a0zYS0/bvMTjRsQ+Q0+fOHHCqDmfToyrei8LK03G5s0t7WsAHiBOSeOnkfmrbBH2h4D1kqVK8MNagaERJmKGavfXa47jUbUriiwNc4h9PbqPrTtjcnPj6J24UUN5l6NmmOapNlU8xx8S3B0aydVuv4PPqM/iDJMYvXnGLqHmNJqJDLCq1hKcEsQGUYNW5lW1a2M1FCSbWtQFHcb9m0j1wOHt0NKbPj+8cYp7oe47itmKFgVI/IOrEQ7Zj51elwUUjpZ9hBzeO9lyTEKr0AVmzKilg4KdhGr4xDv4YXjAODgqTRn4yO42LqGmmmfZ/2l/GpdmVJAYFgwAAAABJRU5ErkJggg==";

        const doc = new jsPDF({ unit: "pt", format: "letter" });
        const pageWidth = doc.internal.pageSize.getWidth();
        let y = 40;

        const logoWidth = 80;
        const logoHeight = 80;
        const textStartX = 40 + logoWidth + 20;
        const headerLines = [
            "Bentick Square, Kingstown, St. Vincent & the Grenadines",
            "Tel: (784) 451-1011, Mobile: (784) 533-1011, WhatsApp: (784) 533-6377",
            "Email: sigmalabsvg@gmail.com",
        ];

        doc.addImage(sigmaLogo, "JPEG", 40, y - 20, logoWidth, logoHeight);
        doc.setFont("helvetica", "bold").setFontSize(14);
        doc.text(headerLines[0], textStartX, y);
        y += 20;
        doc.setFontSize(12).setFont("helvetica", "normal");
        doc.text(headerLines[1], textStartX, y);
        y += 18;
        doc.text(headerLines[2], textStartX, y);

        y += 20;
        doc.setLineWidth(0.5).line(40, y, pageWidth - 40, y);

        y += 20;
        const dateStr = dayjs().format("MMM D, YYYY, h:mm a");
        doc.setFont("helvetica", "bold");
        doc.text(`Date: ${dateStr}`, pageWidth - 40, y, { align: "right" });

        y += 30;
        doc.text("NAME:", 40, y);
        doc.setFont("helvetica", "normal");
        doc.text(`${patient.name ?? ""} ${patient.surname ?? ""}`.trim() || "N/A", 110, y);

        y += 18;
        doc.setFont("helvetica", "bold");
        doc.text("ADDRESS:", 40, y);
        doc.setFont("helvetica", "normal");
        doc.text(patient.address || "N/A", 110, y);

        y += 18;
        const dob = patient.date_of_birth;
        const age = dob ? dayjs().diff(dayjs(dob), "year") : "N/A";
        doc.text(`Age: ${age} years old`, pageWidth - 40, y, { align: "right" });

        y += 30;
        doc.addImage(rxIcon, "JPEG", 40, y - 8, 24, 24);

        y += 30;
        doc.setFont("helvetica", "bold").setFontSize(12);
        doc.text("Lab Information", 40, y);

        y += 18;
        doc.text("Lab Type:", 40, y);
        doc.setFont("helvetica", "normal");
        doc.text(lab.type || "N/A", 140, y);

        y += 18;
        doc.setFont("helvetica", "bold");
        doc.text("Specimen:", 40, y);
        doc.setFont("helvetica", "normal");
        doc.text(lab.specimen || "N/A", 140, y);

        y += 18;
        doc.setFont("helvetica", "bold");
        doc.text("Requested By:", 40, y);
        doc.setFont("helvetica", "normal");
        const requestedBy = userMap[lab.requested_by] || lab.requested_by || "N/A";
        doc.text(requestedBy, 140, y);

        y += 30;
        doc.setFont("helvetica", "bold");
        doc.text("Results / Notes:", 40, y);
        doc.setFont("helvetica", "normal");
        const labData = lab.data || "";
        const splitData = doc.splitTextToSize(labData, pageWidth - 80);
        doc.text(splitData, 140, y);

        y += splitData.length * 14 + 50;
        const sigWidth = 120;
        const centerX = pageWidth / 2;
        const sigX = centerX - sigWidth / 2;

        doc.setLineWidth(0.5).line(sigX, y, sigX + sigWidth, y);
        doc.addImage(signature, "PNG", sigX, y - 40, sigWidth, 40);
        doc.setFont("helvetica", "normal");
        doc.text("Dr. Roger Duncan M.D., MPH, MSc.", centerX, y + 15, {
            align: "center",
        });

        doc.save("lab-details.pdf");
    };


    if (isLoading) {
        return (
            <Center>
                <Spinner size="xl" />
            </Center>
        );
    }

    if (error) {
        return <Text> Error fetching user details: {error.message}</Text>;
    }

    return (
        <>
            <Helmet>
                <title>Lab Details</title>
            </Helmet>
            <Box mt={20} p={4}>
                <Card
                    boxShadow="md"
                    borderRadius="2xl"
                    overflow="hidden"
                    p={4}
                    border="1px solid"
                    borderColor="gray.300"
                >
                    <CardHeader borderBottom="1px solid" borderColor="blue.200">
                        <Flex
                            w="100%"
                            alignItems="center"
                            justifyContent="space-between"
                            gap={4}
                        >
                            <Box flex="1" textAlign="center">
                                <Text fontSize="2xl" fontWeight="bold">
                                    {lab.patient_details?.name} Details
                                </Text>
                            </Box>
                            <Button onClick={() => generatePDF(lab)}>Generate PDF</Button>
                        </Flex>
                    </CardHeader>

                    <CardBody>
                        <Box
                            overflow="auto"
                            border="1px solid"
                            borderRadius="md"
                            borderColor="gray.300"
                        >
                            <Table
                                variant="striped"
                                colorScheme="blue"
                                size="md"
                                borderRadius="md"
                                overflow="hidden"
                            >

                                <Tbody>
                                <Tr>
                                        <Td fontSize="md">
                                            <strong>Patient Name</strong>
                                        </Td>
                                        <Td fontSize="md">
                                            {lab.patient_details?.name}
                                        </Td>
                                    </Tr>

                                    <Tr>
                                        <Td fontSize="md">
                                            <strong>Patient Surname</strong>
                                        </Td>
                                        <Td fontSize="md">
                                            {lab.patient_details?.surname}
                                        </Td>
                                    </Tr>

                                    <Tr>
                                        <Td fontSize="md">
                                            <strong>Patient Age</strong>
                                        </Td>
                                        <Td fontSize="md">
                                            {lab.patient_details?.date_of_birth
                                                ? calculateAge(
                                                      lab.patient_details
                                                          ?.date_of_birth
                                                  )
                                                : "N/A"}
                                        </Td>
                                    </Tr>

                                    <Tr>
                                        <Td fontSize="md">
                                            <strong>Patient Address</strong>
                                        </Td>
                                        <Td fontSize="md">
                                            {lab.patient_details?.address}
                                        </Td>
                                    </Tr>

                                    <Tr>
                                        <Td fontSize="md">
                                            <strong>Date</strong>
                                        </Td>
                                        <Td fontSize="md">{formattedDay}</Td>
                                    </Tr>

                                    <Tr>
                                        <Td fontSize="md">
                                            <strong>Time</strong>
                                        </Td>
                                        <Td fontSize="md">{formattedTime}</Td>
                                    </Tr>

                                    <Tr>
                                        <Td fontSize="md">
                                            <strong>Type</strong>
                                        </Td>
                                        <Td fontSize="md">{lab.type}</Td>
                                    </Tr>

                                    <Tr>
                                        <Td fontSize="md">
                                            <strong>Specimen</strong>
                                        </Td>
                                        <Td fontSize="md">
                                            {lab.specimen}
                                        </Td>
                                    </Tr>

                                    <Tr>
                                        <Td fontSize="md">
                                            <strong>Data</strong>
                                        </Td>
                                        <Td fontSize="md">
                                            {lab.data}
                                        </Td>
                                    </Tr>

                                    {isLoadingUsers ? (
                                        <Tr>
                                            <Td fontSize="md">
                                                <strong>Requested By</strong>
                                            </Td>
                                            <Td fontSize="md">
                                                <Spinner size="sm" />{" "}
                                                {/* Spinner to indicate loading */}
                                            </Td>
                                        </Tr>
                                    ) : (
                                        <Tr>
                                            <Td fontSize="md">
                                                <strong>Requested By</strong>
                                            </Td>
                                            <Td fontSize="md">
                                                {/* Use the userMap to display the username, or show the ID if not found */}
                                                {userMap[
                                                    lab.requested_by
                                                ] || lab.requested_by}
                                            </Td>
                                        </Tr>
                                    )}
                                </Tbody>
                            </Table>
                        </Box>
                    </CardBody>

                    <CardFooter
                        p={4}
                        borderTop="1px solid"
                        borderColor="gray.300"
                    >
                        <Grid
                            templateColumns="repeat(2, 1fr)"
                            gap={4}
                            width="100%"
                        >
                            <GridItem>
                                <ChakraLink
                                    as={Link}
                                    to={`/patients/${patientId}/labs`}
                                    color="blue.500"
                                >
                                    Back to List
                                </ChakraLink>
                            </GridItem>
                            <GridItem textAlign="right">
                                <Button
                                    colorScheme="blue"
                                    onClick={(e) => {
                                        handleUpdateClick(patientId, labId);
                                        e.stopPropagation();
                                    }}
                                >
                                    Update
                                </Button>
                            </GridItem>
                        </Grid>
                    </CardFooter>
                </Card>
            </Box>
        </>
    );
}   

export default LabDetails;